/*
 * CAPTURE-OWNED MODULE
 * Template Editor Page for capture-ui
 * Moved from root /src to enforce repo boundaries
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import TemplateFieldForm from '../components/TemplateFieldForm';
import ZoneEditorOverlay from '../components/ZoneEditorOverlay';
import ReadOnlyZoneLayer from '../components/ReadOnlyZoneLayer';
import OcrWordOverlay from '../components/OcrWordOverlay';
import TemplateZoneViewer from '../components/TemplateZoneViewer';
import { captureAPI } from '../services/api';
import { duplicateChoiceFromUploadError } from '../utils/ingestionGuards';
import { detectOrDefaultRegions } from '../utils/regionDetection';
import { useCanBuildWorkflows } from '../identity/useIsAdmin';
import {
  getTemplateFull, updateZone, createZone, deleteZone, saveTemplate,
  createField, updateField, deleteField, patchZone,
  publishTemplate, unpublishTemplate, testRunTemplate, detectRegions, createDefaultRegions,
  resolveLabel, createFieldOneBox,
} from '../api/captureTemplates';
// Field library now loaded dynamically from API (see fieldLibrary/fieldLibraryCategories state)
import RegionList from '../components/regions/RegionList';
import RegionOverlay from '../components/regions/RegionOverlay';
import RegionExplanation from '../components/regions/RegionExplanation';
import TemplateIdentifiers from '../components/TemplateIdentifiers';
import TestRunPanel from '../components/TestRunPanel';
import TemplateStudioAssistantPanel from '../components/TemplateStudioAssistantPanel';
import AnchorMatchSettings from '../components/right-panel/AnchorMatchSettings';
import FieldPropertiesPanel from '../components/right-panel/FieldPropertiesPanel';
import EditorHeader from '../components/template-editor/EditorHeader';
import DocTypeSelector from '../components/template-editor/DocTypeSelector';
import FieldLibraryPanel from '../components/template-editor/FieldLibraryPanel';
import FieldRow from '../components/template-editor/FieldRow';
import ZonePropertiesPanel from '../components/template-editor/ZonePropertiesPanel';
import DocumentAttributesSection from '../components/template-editor/DocumentAttributesSection';
import LineItemsSection from '../components/template-editor/LineItemsSection';
import OneBoxConfirm, { TYPE_TO_RESCUE_CODES } from '../components/template-editor/OneBoxConfirm';
import useMediaQuery from '../utils/useMediaQuery';
import { isDocumentAttributeKey } from '../utils/documentAttributes';
import { docTypeName } from '../utils/libraryFieldSuggestions';
import { computeZoneTextPreview } from '../utils/zoneTextPreview';
import { cascadeConfirmMessage } from '../utils/zoneDeletion';
import './TemplateEditorPage.css';

/**
 * { code, message } for a one-box refusal or failure: a server refusal keeps
 * its code and message (api/captureTemplates postOrRefusal); anything else is
 * shown with the best text it carries. Never silent.
 */
function oneBoxError(err, fallback) {
  if (err?.refusal) return { code: err.code, message: err.message };
  return { code: null, message: err?.response?.data?.error || err?.message || fallback };
}

// Injected into utils/regionDetection so the detect → default-regions flow is testable.
const regionDetectionApi = { detectRegions, createDefaultRegions, getTemplateFull };

// ── Region color scheme ──
const REGION_COLORS = {
  header: { bg: 'rgba(44, 111, 177, 0.15)', border: '#2C6FB1', label: 'Header', icon: '📦' },
  parties: { bg: 'rgba(168, 85, 247, 0.15)', border: '#a855f7', label: 'Parties', icon: '👥' },
  line_items: { bg: 'rgba(236, 72, 153, 0.15)', border: '#ec4899', label: 'Line Items', icon: '📊' },
  totals: { bg: 'rgba(34, 197, 94, 0.15)', border: '#22c55e', label: 'Totals', icon: '💰' },
  footer: { bg: 'rgba(251, 146, 60, 0.15)', border: '#fb923c', label: 'Footer', icon: '📄' },
  custom: { bg: 'rgba(156, 163, 175, 0.15)', border: '#9ca3af', label: 'Custom', icon: '⚙️' },
};

// Pages of OCR words fetched at once when a sample document loads.
const OCR_WORDS_PARALLEL = 3;

// The editor's snap/band word list, each word stamped with the 0-based page it
// was fetched for (see wordsOnPage in ZoneEditorOverlay).
function tagWordsWithPage(words, pageIndex) {
  return (words || []).map(w => ({ ...w, page_index: pageIndex }));
}

function getRegionColor(regionType) {
  return REGION_COLORS[regionType] || REGION_COLORS.custom;
}

const TemplateEditorPage = ({
  templateId,
  onBack,
  onSave,
}) => {
  // 2026-06-14 B-2: template publish/lifecycle is a builder action (admin||
  // power_user), not admin-only — mirrors the workflow toolbar.
  const canBuild = useCanBuildWorkflows();
  const [template, setTemplate] = useState(null);
  const [regions, setRegions] = useState([]);
  const [fields, setFields] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [showFieldForm, setShowFieldForm] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [viewerDocument, setViewerDocument] = useState(null);
  // Below 1200px the header's Department/Category collapse to a disclosure so
  // the header stays two rows (EditorHeader.css uses the same breakpoint).
  const narrowHeader = useMediaQuery('(max-width: 1199px)');
  // Page count of the sample document, reported by the viewer once the PDF
  // loads. Kept with the document id it belongs to, so a replaced sample
  // never shows the previous document's count.
  const [sampleDocPages, setSampleDocPages] = useState({ docId: null, count: null });
  const [showOcrWords, setShowOcrWords] = useState(true);
  const [showRegions, setShowRegions] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [fieldGrouping, setFieldGrouping] = useState('flat'); // 'flat' | 'grouped'
  const [showZoneTypeField, setShowZoneTypeField] = useState(true);
  const [showZoneTypeAnchor, setShowZoneTypeAnchor] = useState(true);
  const [showZoneTypeRegion, setShowZoneTypeRegion] = useState(true);
  const [showZoneTypeTable, setShowZoneTypeTable] = useState(true);
  // Anchor and region zones are drawn dimmed unless this is on.
  const [showAnchorRegion, setShowAnchorRegion] = useState(false);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  // The sample document's snap/band words, every page, keyed by 0-based page
  // index (see the OCR word cache below). A page that has not loaded is absent.
  const [ocrWordsByPage, setOcrWordsByPage] = useState({ docId: null, pages: {} });
  const [hoveredRegion, setHoveredRegion] = useState(null);

  // Zone editing state
  const [zoneMode, setZoneMode] = useState('select'); // 'select' | 'draw'
  const [editingZoneId, setEditingZoneId] = useState(null);
  const [dirtyZoneIds, setDirtyZoneIds] = useState(new Set());
  const [newZoneIds, setNewZoneIds] = useState(new Set()); // zones created locally, not yet saved
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Publish state
  const [currentHash, setCurrentHash] = useState(null);
  const [publishing, setPublishing] = useState(false);

  // Template name editing state
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [nameSaving, setNameSaving] = useState(false);

  // Draw mode defaults
  const [drawZoneType, setDrawZoneType] = useState('field');
  const [drawFieldId, setDrawFieldId] = useState(null);

  // Field Library state
  const [libAddingKey, setLibAddingKey] = useState(null); // key currently being added

  // Show/hide unzoned fields toggle. Shown by default so a field just added
  // (which has no zone yet) is visible in the list.
  const [showUnzonedFields, setShowUnzonedFields] = useState(true);

  // Left panel tab: 'fields' | 'identifiers'
  const [leftTab, setLeftTab] = useState('fields');

  // Test run state
  const [testRunResult, setTestRunResult] = useState(null);
  const [testRunLoading, setTestRunLoading] = useState(false);
  const [testRunError, setTestRunError] = useState(null);
  const [showTestRun, setShowTestRun] = useState(false);

  // Place Zone mode state (entered after adding a field from library)
  const [placingFieldId, setPlacingFieldId] = useState(null);
  const [placingFieldLabel, setPlacingFieldLabel] = useState(null);
  const [placingBusy, setPlacingBusy] = useState(false); // true while persisting zone

  // Upload sample document state
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const sampleFileInputRef = useRef(null);

  // AI refine state
  const [aiRefineLoading, setAiRefineLoading] = useState(false);
  const [aiRefineError, setAiRefineError] = useState(null);
  const [aiRefineProposal, setAiRefineProposal] = useState(null);
  const [aiRefineHint, setAiRefineHint] = useState('');
  const [showAiRefinePanel, setShowAiRefinePanel] = useState(false);

  // Detect Regions state
  const [detectingRegions, setDetectingRegions] = useState(false);
  const [detectRegionsError, setDetectRegionsError] = useState(null);

  // Identifier word-pick mode: when active, clicking OCR words pre-fills the modal
  const [identifierPickMode, setIdentifierPickMode] = useState(false);
  const [identifierPrefillValue, setIdentifierPrefillValue] = useState('');

  // Field word-pick mode: when active, clicking an OCR word creates a zone for placingFieldId
  const [fieldPickMode, setFieldPickMode] = useState(false);
  // One-box placement awaiting confirmation (OneBoxConfirm), or null
  const [oneBox, setOneBox] = useState(null);
  const oneBoxSeq = useRef(0); // a newer click or drag, or Cancel, discards an older preview

  // Dynamic field library (loaded from API)
  const [fieldLibrary, setFieldLibrary] = useState([]);
  const [fieldLibraryCategories, setFieldLibraryCategories] = useState([]);

  // Deletes persist at once (handleZoneDelete), so they never make the editor dirty.
  const isDirty = dirtyZoneIds.size > 0 || newZoneIds.size > 0;

  // Upload sample document handler
  const handleSampleDocumentUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !template) return;
    // Reset input so the same file can be re-selected
    e.target.value = '';

    setUploadingDoc(true);
    setError(null);
    try {
      // 1. Upload file to create a capture_documents row. Gate C: replacing a
      //    template's sample with a file that matches an existing document is a
      //    deliberate "use THIS file" action — there's no meaningful "use
      //    existing" here — so on a duplicate block, proceed as new (override).
      let uploadRes;
      try {
        uploadRes = await captureAPI.uploadFile(file);
      } catch (uploadErr) {
        if (duplicateChoiceFromUploadError(uploadErr)) {
          console.log('[TemplateEditor] Sample matches an existing document — uploading as new (replace-source intent)');
          uploadRes = await captureAPI.uploadFile(file, { overrideDuplicate: true });
        } else {
          throw uploadErr;
        }
      }
      const newDocId = uploadRes?.document?.id || uploadRes?.captureDocument?.id;
      if (!newDocId) throw new Error('Upload succeeded but no document ID returned');

      // 2. Poll for OCR completion (wait for words to appear)
      const pollStart = Date.now();
      const POLL_TIMEOUT = 90_000;
      const POLL_INTERVAL = 2_000;
      let ocrReady = false;
      while (Date.now() - pollStart < POLL_TIMEOUT) {
        try {
          const stats = await captureAPI.getOcrWordStats({ documentId: newDocId });
          if (stats?.totalWords > 0) { ocrReady = true; break; }
        } catch { /* ignore poll errors */ }
        await new Promise(r => setTimeout(r, POLL_INTERVAL));
      }
      if (!ocrReady) {
        console.warn('[TemplateEditor] OCR poll timed out for doc', newDocId);
      }

      // 3. Link the new document to the template
      await saveTemplate(template.id, { source_document_id: newDocId });

      // 4. Get the signed URL and update the viewer
      const urlRes = await captureAPI.getDocumentFileUrl(newDocId);
      setViewerDocument({ id: newDocId, downloadUrl: urlRes.url, filename: file.name });

      // 5. OCR words: the word cache loads every page of the new document.

      // Update local template state with new source_document_id
      setTemplate(prev => prev ? { ...prev, source_document_id: newDocId } : prev);
    } catch (err) {
      console.error('[TemplateEditor] Sample document upload failed:', err);
      setError(err?.message || 'Failed to upload sample document');
    } finally {
      setUploadingDoc(false);
    }
  }, [template]);

  // Test run handler
  const handleTestRun = useCallback(async () => {
    if (!template) return;
    setTestRunLoading(true);
    setTestRunError(null);
    setTestRunResult(null);
    setShowTestRun(true);
    try {
      const result = await testRunTemplate(template.id, {
        documentId: viewerDocument?.id || undefined,
      });
      if (result.ok) {
        setTestRunResult(result);
      } else {
        setTestRunError(result.error || 'Test run failed');
      }
    } catch (err) {
      setTestRunError(err?.response?.data?.error || err?.message || 'Test run failed');
    } finally {
      setTestRunLoading(false);
    }
  }, [template, viewerDocument]);

  // When user clicks a field in test results, select it in the editor
  const handleTestRunFieldClick = useCallback((fieldKey) => {
    const field = fields.find(f => f.field_key === fieldKey);
    if (field) {
      // Close test panel so Properties panel becomes visible
      setShowTestRun(false);

      setSelectedField(field);
      setSelectedRegion(null);

      // Find and select the anchor zone for this field (most likely the problem zone)
      const anchorZone = zones.find(z => z.field_id === field.id && z.zone_type === 'anchor');
      const fieldZone = zones.find(z => z.field_id === field.id && z.zone_type === 'field');
      const targetZone = anchorZone || fieldZone || zones.find(z => z.field_id === field.id);

      if (targetZone) {
        setEditingZoneId(targetZone.id);
      } else {
        setEditingZoneId(null);
      }
    }
  }, [fields, zones]);

  // Filter zones for display — hide anchor zones unless Advanced mode is on
  const visibleZones = useMemo(() => {
    // Anchor and region zones are always drawn (dimmed unless "Show anchor and
    // region" is on) so they can be seen and deleted; the per-type filters
    // under Advanced still hide a type outright. Table zones have their own
    // filter (they used to go with Fields).
    return zones.filter(z => {
      if (z.zone_type === 'field') return showZoneTypeField;
      if (z.zone_type === 'line_items_table') return showZoneTypeTable;
      if (z.zone_type === 'anchor') return showZoneTypeAnchor;
      if (z.zone_type === 'region') return showZoneTypeRegion;
      return true;
    });
  }, [zones, showZoneTypeField, showZoneTypeTable, showZoneTypeAnchor, showZoneTypeRegion]);

  const isPlacing = !!placingFieldId;

  // Enter Place Zone mode for a specific field.
  // If OCR words are available, default to field-pick (click) mode.
  // The banner lets the user switch to draw mode if preferred.
  const startPlaceZone = useCallback((fieldId, label) => {
    setPlacingFieldId(fieldId);
    setPlacingFieldLabel(label || 'field');
    setFieldPickMode(true); // Default to click mode when OCR words are present
    console.log(`[TemplateEditor] Place Zone mode (click): fieldId=${fieldId} label=${label}`);
  }, []);

  // Cancel Place Zone mode (both click and draw variants)
  const cancelPlaceZone = useCallback(() => {
    setPlacingFieldId(null);
    setPlacingFieldLabel(null);
    setFieldPickMode(false);
    console.log('[TemplateEditor] Place Zone mode cancelled');
  }, []);

  // The editor fills the viewport below the app chrome; its three panels
  // scroll inside themselves, never the page. Same body class as the Review
  // page (`body.app-viewport-locked` in index.css).
  useEffect(() => {
    document.body.classList.add('app-viewport-locked');
    return () => document.body.classList.remove('app-viewport-locked');
  }, []);

  // Esc key handler for Place Zone mode, field pick mode, and identifier pick mode
  useEffect(() => {
    if (!isPlacing && !identifierPickMode && !fieldPickMode) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (identifierPickMode) setIdentifierPickMode(false);
        if (fieldPickMode) setFieldPickMode(false);
        if (isPlacing) cancelPlaceZone();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isPlacing, identifierPickMode, fieldPickMode, cancelPlaceZone]);

  // Load template data (full: template + fields + zones + transforms)
  // IMPORTANT: This callback must only close over `templateId` (stable prop).
  // Do NOT add other state variables to the dependency array or close over them
  // here — doing so causes loadTemplate to be recreated on every render, which
  // triggers the useEffect below to re-invoke loadTemplate, causing duplicate
  // network calls (and potential 401s during the async getSession() race).
  const loadTemplate = useCallback(async () => {
    if (!templateId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log(`[TemplateEditor] Loading template full: ${templateId}`);
      const response = await getTemplateFull(templateId);
      
      if (response.ok && response.template) {
        const tpl = response.template;
        setTemplate(tpl);
        setRegions(response.regions || []);
        setFields(response.fields || []);
        setZones(response.zones || []);
        setDirtyZoneIds(new Set());
        setNewZoneIds(new Set());
        setCurrentHash(response.currentHash || null);

        const loadedRegions = response.regions || [];
        const loadedFields = response.fields || [];
        const loadedZones = response.zones || [];
        const zonedFieldIds = new Set(loadedZones.filter(z => z.field_id).map(z => z.field_id));
        const tableZones = loadedZones.filter(z => z.zone_type === 'line_items_table');
        console.log('[TemplateEditor] Template loaded:', {
          id: tpl.id,
          name: tpl.name,
          regions: loadedRegions.length,
          fields: loadedFields.length,
          zones: loadedZones.length,
          zonesWithFieldId: loadedZones.filter(z => z.field_id).length,
          tableZones: tableZones.length,
          fieldsWithZones: zonedFieldIds.size,
          fieldsWithoutZones: loadedFields.filter(f => !zonedFieldIds.has(f.id) && !f.is_line_items).length,
          source_document_id: tpl.source_document_id || '(none)',
        });

        // Set default drawFieldId to first field if available.
        // Uses functional updater so this does NOT create a closure dependency on drawFieldId.
        const firstDrawable = (response.fields || []).find(f => !isDocumentAttributeKey(f.field_key));
        if (firstDrawable) {
          setDrawFieldId(prev => prev || firstDrawable.id);
        }

        // Fetch signed PDF URL for the source document
        const docId = tpl.source_document_id;
        if (!docId) {
          console.warn('[TemplateEditor] Template has no source_document_id — no PDF to display');
          setViewerDocument(null);
        } else {
          try {
            const fileUrlResp = await captureAPI.getDocumentFileUrl(docId);
            console.log('[TemplateEditor] Signed URL obtained for doc:', docId);
            setViewerDocument({
              id: docId,
              downloadUrl: fileUrlResp.url,
              filename: fileUrlResp.filename || tpl.name,
            });
          } catch (docErr) {
            const status = docErr.response?.status || docErr.status;
            if (status === 404) {
              setError(`Source document not found (id: ${docId}). It may have been deleted.`);
            } else if (status === 403) {
              setError('Not authorized to access the source document. Check tenant context (Auth Diag).');
            } else {
              setError(`Failed to load document PDF: ${docErr.message}`);
            }
            setViewerDocument(null);
          }
        }
      } else {
        throw new Error(response.error || 'Failed to load template');
      }
    } catch (err) {
      console.error('[TemplateEditor] Error loading template:', err);
      const status = err.response?.status || err.status;
      if (status === 404) {
        setError(`Template not found (id: ${templateId}).`);
      } else if (status === 403) {
        setError('Not authorized. Check tenant context (Auth Diag).');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [templateId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  // Load field library from API. Also re-read after a field is linked to this
  // template's document type, so the linked flag, the "Field library" badge
  // and the panel row are right without a reload. Resolves to the active rows
  // (null when the read failed).
  const loadFieldLibrary = useCallback(async () => {
    // Capture authoring surface — template/extraction targets must never include
    // library-only fields, so request the capture scope explicitly.
    try {
      const resp = await captureAPI.getFieldLibrary?.('capture');
      const allFields = resp?.fields || [];
      const active = allFields.filter(f => f.is_active);
      setFieldLibrary(active);
      const cats = [...new Set(active.map(f => f.category))];
      setFieldLibraryCategories(cats);
      return active;
    } catch (err) {
      console.warn('[TemplateEditor] Failed to load field library:', err);
      return null;
    }
  }, []);

  useEffect(() => { loadFieldLibrary(); }, [loadFieldLibrary]);

  const viewerDocId = viewerDocument?.id ?? null;
  const sampleDocPageCount = sampleDocPages.docId === viewerDocId ? sampleDocPages.count : null;
  const handleViewerNumPages = useCallback((count) => {
    setSampleDocPages({ docId: viewerDocId, count });
  }, [viewerDocId]);

  // OCR word cache for snap-assist and band drags: EVERY page of the sample
  // document is fetched when it loads (at most OCR_WORDS_PARALLEL at a time),
  // so a press on any page, including one it activates, has that page's words.
  // Each word is tagged with its page. One load per (document, page): loads
  // maps page index -> promise of the tagged words (null on failure, and the
  // entry is dropped so a later call retries). Results for a document that is
  // no longer shown are ignored.
  const wordsLoadsRef = useRef({ docId: null, loads: new Map() });
  const currentPageIndexRef = useRef(0);
  currentPageIndexRef.current = (currentPageNumber || 1) - 1;

  const loadPageWords = useCallback((docId, pageIndex) => {
    const cache = wordsLoadsRef.current;
    if (!docId || cache.docId !== docId) return Promise.resolve(null);
    if (cache.loads.has(pageIndex)) return cache.loads.get(pageIndex);
    const load = captureAPI.getOcrWords({ documentId: docId, pageIndex, engine: 'paddle' })
      .then((resp) => {
        if (!resp?.ok) throw new Error('OCR words request failed');
        const words = tagWordsWithPage(resp.words, pageIndex);
        if (wordsLoadsRef.current.docId === docId) {
          setOcrWordsByPage(prev => (prev.docId === docId
            ? { docId, pages: { ...prev.pages, [pageIndex]: words } }
            : prev));
        }
        return words;
      })
      .catch(() => {
        if (wordsLoadsRef.current.docId === docId) wordsLoadsRef.current.loads.delete(pageIndex);
        return null;
      });
    cache.loads.set(pageIndex, load);
    return load;
  }, []);

  // A new sample document starts an empty cache and loads the shown page first.
  useEffect(() => {
    wordsLoadsRef.current = { docId: viewerDocId, loads: new Map() };
    setOcrWordsByPage({ docId: viewerDocId, pages: {} });
    if (viewerDocId) loadPageWords(viewerDocId, currentPageIndexRef.current);
  }, [viewerDocId, loadPageWords]);

  // The active page is always loaded (also retries a page whose load failed).
  useEffect(() => {
    if (viewerDocId) loadPageWords(viewerDocId, (currentPageNumber || 1) - 1);
  }, [viewerDocId, currentPageNumber, loadPageWords]);

  // Once the page count is known, every other page, OCR_WORDS_PARALLEL at a time.
  useEffect(() => {
    if (!viewerDocId || !sampleDocPageCount) return undefined;
    const queue = Array.from({ length: sampleDocPageCount }, (_, i) => i);
    let cancelled = false;
    const worker = async () => {
      while (!cancelled && queue.length) await loadPageWords(viewerDocId, queue.shift());
    };
    for (let k = 0; k < Math.min(OCR_WORDS_PARALLEL, queue.length); k += 1) worker();
    return () => { cancelled = true; };
  }, [viewerDocId, sampleDocPageCount, loadPageWords]);

  const wordsByPage = ocrWordsByPage.docId === viewerDocId ? ocrWordsByPage.pages : {};
  // The active page's words, for the zone text preview and the properties panel.
  const ocrWords = wordsByPage[(currentPageNumber || 1) - 1] ?? null;
  const loadWordsForPage = useCallback((pageIndex) => loadPageWords(viewerDocId, pageIndex), [loadPageWords, viewerDocId]);

  // Field create/update: persist immediately to backend
  const handleFieldSave = useCallback(async (fieldData) => {
    if (!template) return;
    console.log('[TemplateEditor] Field save:', fieldData);
    try {
      let saved;
      if (fieldData.id) {
        // Update existing field
        saved = await updateField(template.id, fieldData.id, {
          field_key: fieldData.field_key,
          label: fieldData.label,
          value_type: fieldData.value_type,
          required: fieldData.required,
        });
      } else {
        // Create new field
        saved = await createField(template.id, {
          field_key: fieldData.field_key,
          label: fieldData.label,
          value_type: fieldData.value_type,
          required: fieldData.required,
        });
      }
      // Re-fetch to get updated fields list
      const fresh = await getTemplateFull(template.id);
      if (fresh.ok) {
        setFields(fresh.fields || []);
        setZones(fresh.zones || []);
      }
      setShowFieldForm(false);
      setEditingField(null);
      // TemplateFieldForm saves transforms with the id of the field returned
      // here (savedField.id). Without it a new field's transforms were
      // silently dropped. The route answers { ok, field }; the refetched row
      // is the fallback.
      return saved?.field
        ?? (fieldData.id
          ? (fresh.fields || []).find(f => f.id === fieldData.id)
          : (fresh.fields || []).find(f => f.field_key === fieldData.field_key))
        ?? null;
    } catch (err) {
      console.error('[TemplateEditor] Field save failed:', err);
      throw err; // Let TemplateFieldForm show the error
    }
  }, [template]);

  // Field delete: cascade-delete field + zones + transforms via backend
  const handleFieldDelete = useCallback(async (fieldId) => {
    if (!template) return;
    const field = fields.find(f => f.id === fieldId);
    if (!field) return;

    const zoneCount = zones.filter(z => z.field_id === fieldId).length;
    const label = field.label || field.field_key;

    // Build descriptive confirmation message
    const isLineItems = field.field_key === 'line_items' || field.is_line_items;
    let confirmMessage = `Delete field "${label}"?\n\nThis will permanently remove:\n`;
    confirmMessage += `\u2022 The field definition\n`;
    confirmMessage += `\u2022 ${zoneCount} zone(s) (region, anchor, field)\n`;
    confirmMessage += `\u2022 All associated transforms\n`;
    if (isLineItems) {
      confirmMessage += `\n\u26A0\uFE0F WARNING: This is a line items field!\n`;
      confirmMessage += `Deleting it will prevent table extraction on future documents.\n`;
    }
    confirmMessage += `\nThis action cannot be undone.`;
    if (!window.confirm(confirmMessage)) return;

    // Optimistic update
    const prevFields = fields;
    const prevZones = zones;
    setFields(prev => prev.filter(f => f.id !== fieldId));
    setZones(prev => prev.filter(z => z.field_id !== fieldId));
    if (selectedField?.id === fieldId) setSelectedField(null);
    if (drawFieldId === fieldId) setDrawFieldId(null);
    if (editingZoneId && prevZones.find(z => z.id === editingZoneId && z.field_id === fieldId)) {
      setEditingZoneId(null);
    }

    try {
      const result = await deleteField(template.id, fieldId);
      console.log(`[TemplateEditor] Field deleted id=${fieldId} key=${field.field_key} zonesDeleted=${result?.zonesDeleted ?? 0} transformsDeleted=${result?.transformsDeleted ?? 0}`);
      // Re-fetch to sync state fully
      const fresh = await getTemplateFull(template.id);
      if (fresh.ok) {
        setFields(fresh.fields || []);
        setZones(fresh.zones || []);
        setCurrentHash(fresh.currentHash || null);
      }
    } catch (err) {
      // Revert optimistic update on error
      setFields(prevFields);
      setZones(prevZones);
      const msg = err?.response?.data?.error || err?.message || 'Failed to delete field';
      console.error('[TemplateEditor] Field delete failed:', msg);
      setError(msg);
    }
  }, [template, fields, zones, selectedField, drawFieldId, editingZoneId]);

  // Field region reassignment: persist immediately via PATCH
  const handleFieldRegionChange = useCallback(async (fieldId, newRegionId) => {
    if (!template) return;
    try {
      const result = await updateField(template.id, fieldId, { region_id: newRegionId });
      if (result.ok && result.field) {
        setFields(prev => prev.map(f => 
          f.id === fieldId ? { ...f, region_id: newRegionId } : f
        ));
      }
    } catch (err) {
      console.error('[TemplateEditor] Failed to update field region:', err);
      setError(err?.response?.data?.error || err?.message || 'Failed to update field region');
    }
  }, [template]);

  // Toggle field enabled/disabled: persist immediately
  const handleFieldToggleEnabled = useCallback(async (fieldId, currentEnabled) => {
    if (!template) return;
    try {
      await updateField(template.id, fieldId, { enabled: !currentEnabled });
      const fresh = await getTemplateFull(template.id);
      if (fresh.ok) {
        setFields(fresh.fields || []);
        setCurrentHash(fresh.currentHash || null);
      }
      console.log(`[TemplateEditor] Field ${fieldId} enabled=${!currentEnabled}`);
    } catch (err) {
      console.error('[TemplateEditor] Toggle enabled failed:', err);
      setError(err?.response?.data?.error || err?.message || 'Failed to toggle field');
    }
  }, [template]);

  // One-box placement (D10). A clicked word (padded) or a dragged box is sent
  // to the server, which reads the label next to the value
  // (POST .../zones/resolve-label). Nothing is written until the user confirms
  // with Use in OneBoxConfirm; Use creates the value zone and its anchor zone
  // in one write (POST .../fields/:fieldId/one-box). A later click or drag in
  // Place Zone mode replaces the preview.
  const startOneBox = useCallback(async (page_index, box) => {
    // A click or drag during a save must not replace the saving state.
    if (!template || !placingFieldId || oneBox?.status === 'saving') return;
    const seq = ++oneBoxSeq.current;
    const base = {
      fieldId: placingFieldId, fieldLabel: placingFieldLabel, page_index, box,
      preview: null, error: null, choice: null, typed: '', noLabel: false, positionOnly: false,
    };
    setOneBox({ ...base, status: 'resolving' });
    try {
      const preview = await resolveLabel(template.id, { page_index, box, field_id: placingFieldId });
      if (seq !== oneBoxSeq.current) return;
      console.log(`[TemplateEditor] One-box preview field=${placingFieldId} value="${preview?.value?.text}" label="${preview?.label?.text}" kind=${preview?.label?.kind} confidence=${preview?.confidence}`);
      setOneBox({ ...base, status: 'ready', preview });
    } catch (err) {
      if (seq !== oneBoxSeq.current) return;
      const error = oneBoxError(err, 'Could not read the label next to this value');
      console.warn(`[TemplateEditor] One-box preview refused field=${placingFieldId}: ${error.code || 'error'} ${error.message}`);
      // No usable label beside the value: a typed label or position only may place it.
      setOneBox({ ...base, status: 'refused', error, noLabel: TYPE_TO_RESCUE_CODES.has(error.code) });
    }
  }, [template, placingFieldId, placingFieldLabel, oneBox]);

  const cancelOneBox = useCallback(() => {
    oneBoxSeq.current += 1;
    setOneBox(null);
    cancelPlaceZone();
  }, [cancelPlaceZone]);

  // The field's saved value and anchor zones: the server refuses a second one
  // (409 field_has_zone), so Use deletes these first.
  const placedZonesOf = useCallback(
    (fieldId) => zones.filter(z => z.field_id === fieldId
      && (z.zone_type === 'field' || z.zone_type === 'anchor')
      && !String(z.id).startsWith('temp-')),
    [zones]
  );

  const handleOneBoxUse = useCallback(async () => {
    if (!oneBox || !template || oneBox.status === 'saving' || oneBox.status === 'resolving') return;
    const { fieldId, page_index, box, choice, typed, noLabel, positionOnly } = oneBox;
    // Position only: the value zone alone, with no label (offered only when no label was found).
    const position_only = !!(noLabel && positionOnly);
    // label_text only for a label the user chose: an alternative or typed text.
    const label_text = position_only ? undefined : (typed.trim() || choice || undefined);
    setOneBox(prev => prev && ({ ...prev, status: 'saving', error: null }));
    const settle = (error) => setOneBox(prev => prev && ({ ...prev, status: prev.preview ? 'ready' : 'refused', error }));
    const dropRemoved = (removed) => {
      if (removed.size === 0) return;
      setZones(prev => prev.filter(z => !removed.has(z.id)));
      setDirtyZoneIds(prev => new Set([...prev].filter(id => !removed.has(id))));
    };

    // 1. Replace: delete the field's current zones (the resolve preview ran first).
    const removed = new Set();
    for (const z of placedZonesOf(fieldId)) {
      if (removed.has(z.id)) continue;
      try {
        const res = await deleteZone(template.id, z.id);
        for (const id of (res?.deletedZoneIds || [z.id])) removed.add(id);
      } catch (err) {
        if (err?.response?.status === 404) { removed.add(z.id); continue; }
        dropRemoved(removed);
        settle({ code: null, message: `Could not remove this field's current zone: ${err?.response?.data?.error || err?.message || 'request failed'}` });
        return;
      }
    }

    // 2. Create both zones in one server write.
    try {
      const res = await createFieldOneBox(template.id, fieldId, { page_index, box, label_text, position_only });
      console.log(`[TemplateEditor] One-box created field=${fieldId} value_zone=${res?.value_zone?.id} anchor_zone=${res?.anchor_zone?.id ?? 'none'} ${res?.position_only ? 'position_only' : `label="${res?.label?.text}"`}${removed.size ? ` replaced=${removed.size}` : ''}`);
      setZones(prev => [...prev.filter(z => !removed.has(z.id)), res.value_zone, res.anchor_zone].filter(Boolean));
      setDirtyZoneIds(prev => new Set([...prev].filter(id => !removed.has(id))));
      if (res?.value_zone?.id) setEditingZoneId(res.value_zone.id);
      oneBoxSeq.current += 1;
      setOneBox(null);
      cancelPlaceZone();
      const fresh = await getTemplateFull(template.id);
      if (fresh.ok) {
        setZones(fresh.zones || []);
        setCurrentHash(fresh.currentHash || null);
      }
    } catch (err) {
      dropRemoved(removed);
      const error = oneBoxError(err, 'Could not create the zones');
      if (error.code === 'field_has_zone') {
        // Zones this page did not know about: load them so Use replaces them next time.
        try {
          const fresh = await getTemplateFull(template.id);
          if (fresh.ok) { setZones(fresh.zones || []); setCurrentHash(fresh.currentHash || null); }
          error.message = `${error.message} Use again to replace them.`;
        } catch { /* the message alone still explains the refusal */ }
      }
      console.warn(`[TemplateEditor] One-box create refused field=${fieldId}: ${error.code || 'error'} ${error.message}`);
      settle(error);
    }
  }, [oneBox, template, placedZonesOf, cancelPlaceZone]);

  // Field word-pick: the clicked OCR word, padded to avoid tight-boundary
  // misses, is the one box, on the page of the overlay the word was clicked in.
  const handleFieldWordClick = useCallback((word, pageIndex = (currentPageNumber || 1) - 1) => {
    if (!fieldPickMode || !placingFieldId || !template || oneBox?.status === 'saving') return;
    const bbox = word.bbox;
    if (!bbox) return;
    const PAD = 0.005; // normalized padding (~6px at typical render size)
    const x = Math.max(0, bbox.x1 - PAD);
    const y = Math.max(0, bbox.y1 - PAD);
    const w = Math.min(1 - x, (bbox.x2 - bbox.x1) + PAD * 2);
    const h = Math.min(1 - y, (bbox.y2 - bbox.y1) + PAD * 2);
    startOneBox(pageIndex, { x, y, w, h });
  }, [fieldPickMode, placingFieldId, template, oneBox, currentPageNumber, startOneBox]);

  // Esc cancels a one-box confirmation, without a write (not while saving).
  useEffect(() => {
    if (!oneBox) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape' && oneBox.status !== 'saving') {
        e.preventDefault();
        cancelOneBox();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [oneBox, cancelOneBox]);

  // Field Library: add or re-enable a field from the library, then enter Place Zone mode
  const handleLibraryAdd = useCallback(async (libItem) => {
    if (!template) return;
    if (!libItem?.field_key) return;
    const normalizedKey = libItem.field_key.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
    // Document attributes are never template fields (utils/documentAttributes).
    if (isDocumentAttributeKey(libItem.field_key) || isDocumentAttributeKey(normalizedKey)) return;
    const existing = fields.find(f => f.field_key === normalizedKey);
    const isLineItems = libItem.value_type === 'table';

    setLibAddingKey(normalizedKey);
    try {
      if (existing) {
        if (existing.enabled === false) {
          // Re-enable the existing disabled field
          await updateField(template.id, existing.id, { enabled: true });
          console.log(`[TemplateEditor] Library: re-enabled ${normalizedKey}`);
        } else {
          // Already exists and enabled — nothing to do
          console.log(`[TemplateEditor] Library: ${normalizedKey} already added`);
          return;
        }
      } else {
        // Compute sort_order: max existing + 10, or use sortHint
        const maxSort = fields.reduce((m, f) => Math.max(m, f.sort_order ?? 0), 0);
        await createField(template.id, {
          field_key: normalizedKey,
          label: libItem.label,
          value_type: libItem.value_type === 'table' ? 'text' : libItem.value_type,
          required: libItem.default_required,
          // Always enabled: adding it from the Studio is the request to extract
          // it. libItem.default_enabled only says whether the doc type turns it
          // on by default (and is false for every custom library field).
          enabled: true,
          is_line_items: isLineItems,
          sort_order: maxSort + 10,
        });
        console.log(`[TemplateEditor] Library: created ${normalizedKey}`);
      }
      // Re-fetch fields
      const fresh = await getTemplateFull(template.id);
      if (fresh.ok) {
        setFields(fresh.fields || []);
        setZones(fresh.zones || []);
        setCurrentHash(fresh.currentHash || null);

        // Enter Place Zone mode only for an enabled field that takes a zone:
        // never for line items, never for a field that ended up disabled.
        const addedField = (fresh.fields || []).find(f => f.field_key === normalizedKey);
        if (addedField && !isLineItems && !addedField.is_line_items && addedField.enabled !== false) {
          startPlaceZone(addedField.id, libItem.label);
        }
      }
    } catch (err) {
      console.error('[TemplateEditor] Library add failed:', err);
      setError(err?.response?.data?.error || err?.message || 'Failed to add field');
    } finally {
      setLibAddingKey(null);
    }
  }, [template, fields, startPlaceZone]);

  // Link a library field to this template's document type
  // (POST /templates/field-library/:key/associations; inserts default_enabled
  // false, never touches PATCH /field-library). 201 and 409 (already linked)
  // are success. Any other failure except 403 re-reads the library once: if
  // the link is there after all (a lost response, a concurrent click), that is
  // success too.
  const linkLibraryField = useCallback(async (fieldKey, docType, label) => {
    try {
      await captureAPI.addFieldDocTypeAssociation(fieldKey, docType, null);
      return { ok: true, refreshed: false };
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) return { ok: true, refreshed: false };
      if (status !== 403) {
        const rows = await loadFieldLibrary();
        if (rows && rows.some(r => r.field_key === fieldKey && r.doc_type === docType)) {
          return { ok: true, refreshed: true };
        }
      }
      const serverMessage = err?.response?.data?.error || err?.response?.data?.details || err?.message || 'request failed';
      return { ok: false, message: `Could not link ${label} to ${docTypeName(docType)}: ${serverMessage}` };
    }
  }, [loadFieldLibrary]);

  // "Use library field" / "Link to <type> and use" in the Add Field form.
  // Order: link (if needed) -> re-read the library -> close the form -> add the
  // template field through handleLibraryAdd (enabled, Place Zone, PR #52). A
  // failed link stops here: nothing is created and the form stays open with
  // the error on the row. A failed add after a successful link keeps the link
  // (the field is then an ordinary linked library field for this type) and
  // shows the page-level error, as handleLibraryAdd does.
  const handleUseLibraryField = useCallback(async (row, { needsLink = false, label } = {}) => {
    const docType = template?.document_type;
    let item = row;
    if (needsLink && docType) {
      const linked = await linkLibraryField(row.field_key, docType, label || row.label || row.field_key);
      if (!linked.ok) return linked;
      if (!linked.refreshed) await loadFieldLibrary();
      // The new association is is_required false; never copy another type's required flag.
      item = { ...row, doc_type: docType, default_required: false };
    }
    setShowFieldForm(false);
    setEditingField(null);
    await handleLibraryAdd(item);
    return { ok: true };
  }, [template, linkLibraryField, loadFieldLibrary, handleLibraryAdd]);

  // Apply a refetched template (after region detection) to editor state.
  const applyFreshTemplate = useCallback((fresh) => {
    if (!fresh?.ok) return;
    setTemplate(fresh.template);
    setRegions(fresh.regions || []);
    setFields(fresh.fields || []);
    setZones(fresh.zones || []);
    setCurrentHash(fresh.currentHash || null);
  }, []);

  // Field grouping toggle: switch to the "By region" view. If the template has
  // no regions yet, run the existing Organize flow first (AI detect → default
  // regions fallback), then switch. Regions persist untouched in the background.
  const handleSelectByRegion = useCallback(async () => {
    if (regions.length > 0) {
      setFieldGrouping('grouped');
      return;
    }
    if (!template) return;
    setDetectingRegions(true);
    setDetectRegionsError(null);
    try {
      const result = await detectOrDefaultRegions({
        templateId: template.id,
        hasSourceDocument: !!template.source_document_id,
        api: regionDetectionApi,
      });
      if (!result.ok) {
        setDetectRegionsError(result.error);
        return;
      }
      applyFreshTemplate(result.fresh);
      setFieldGrouping('grouped');
    } finally {
      setDetectingRegions(false);
    }
  }, [template, regions, applyFreshTemplate]);

  // Shared "Detect Regions" handler for the top-bar button. Runs AI region
  // detection (or default regions when there's no source document), then FULLY
  // refetches the template so the editor reflects the new regions/fields/zones
  // instead of going stale. Same flow as handleSelectByRegion (the "By region"
  // toggle), minus the view switch. A missing sample document stops with its
  // message and never falls back to default regions (utils/regionDetection.js).
  const handleDetectRegions = useCallback(async () => {
    if (!template) return;
    setDetectingRegions(true);
    setDetectRegionsError(null);
    try {
      const result = await detectOrDefaultRegions({
        templateId: template.id,
        hasSourceDocument: !!template.source_document_id,
        api: regionDetectionApi,
      });
      if (!result.ok) {
        setDetectRegionsError(result.error);
        return;
      }
      applyFreshTemplate(result.fresh);
    } finally {
      setDetectingRegions(false);
    }
  }, [template, applyFreshTemplate]);

  // Field row edit: shared by the flat list and RegionList (grouped view).
  const handleFieldEdit = useCallback((field) => {
    setEditingField(field);
    setShowFieldForm(true);
  }, []);

  // Field row delete: shared by the flat list and RegionList. Same behavior as
  // the original inline row handler — if the field has zones, offer to clear
  // zones only (field stays); otherwise delete the field entirely.
  const handleFieldRowDelete = useCallback((field, fieldZones = []) => {
    const hasZones = fieldZones.length > 0;
    if (hasZones) {
      // Field has zones — offer to clear zones only (returns field to unzoned state)
      // or delete the field entirely
      const choice = window.confirm(
        `"${field.label || field.field_key}" has ${fieldZones.length} zone${fieldZones.length !== 1 ? 's' : ''}.\n\n` +
        `OK — Remove zones only (field stays, can be re-placed)\n` +
        `Cancel — Keep as-is`
      );
      if (choice) {
        // Delete each zone individually, leave the field row intact
        Promise.all(
          fieldZones.map(z => deleteZone(template.id, z.id).catch(() => null))
        ).then(() => {
          setZones(prev => prev.filter(z => z.field_id !== field.id));
          if (editingZoneId && fieldZones.some(z => z.id === editingZoneId)) setEditingZoneId(null);
        });
      }
    } else {
      handleFieldDelete(field.id);
    }
  }, [template, editingZoneId, handleFieldDelete]);

  // Zone commit: update local state + mark dirty
  const handleZoneCommit = useCallback((zoneData) => {
    setZones(prev => prev.map(z => z.id === zoneData.id ? { ...z, ...zoneData } : z));
    if (!newZoneIds.has(zoneData.id)) {
      setDirtyZoneIds(prev => new Set([...prev, zoneData.id]));
    }
  }, [newZoneIds]);

  // Zone create: add to local state, mark as new
  // In Place Zone mode OR line_items_table: immediately persist via API
  const handleZoneCreate = useCallback(async (zoneData) => {
    const isTableZone = zoneData.zone_type === 'line_items_table';

    // Place Zone mode (the drag in click mode, or draw mode): the dragged box
    // is the one box; nothing is written until the user confirms (startOneBox).
    if (placingFieldId && !isTableZone && template) {
      startOneBox(zoneData.page_index ?? 0, { x: zoneData.x, y: zoneData.y, w: zoneData.w, h: zoneData.h });
      return;
    }

    // Immediate-persist path: table zone
    if (isTableZone && template) {
      if (placingBusy) return; // guard double-draw while persisting
      setPlacingBusy(true);
      const payload = {
        field_id: undefined,
        zone_type: zoneData.zone_type || 'field',
        page_index: zoneData.page_index ?? 0,
        x: zoneData.x, y: zoneData.y, w: zoneData.w, h: zoneData.h,
      };
      console.log('[TemplateEditor] Zone save payload:', payload);
      try {
        const res = await createZone(template.id, payload);
        const savedZone = res?.zone || res;
        console.log('[TemplateEditor] Zone save response:', savedZone);
        if (savedZone?.id) {
          setZones(prev => [...prev, savedZone]);
          setEditingZoneId(savedZone.id);
          console.log(`[TemplateEditor] Zone created id=${savedZone.id} type=${savedZone.zone_type}`);
        }
        // Re-fetch to sync hashes
        const fresh = await getTemplateFull(template.id);
        if (fresh.ok) {
          setZones(fresh.zones || []);
          setCurrentHash(fresh.currentHash || null);
        }
      } catch (err) {
        console.error('[TemplateEditor] Zone create failed:', err);
        setError(err?.response?.data?.error || err?.message || 'Failed to create zone');
        setPlacingBusy(false);
        return;
      }
      setPlacingBusy(false);
      // Auto-switch to select after drawing table zone
      setZoneMode('select');
      return;
    }

    // Normal draw mode: add to local state with temp ID
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const newZone = { ...zoneData, id: tempId };
    console.log('[TemplateEditor] Zone created locally:', tempId, zoneData);
    setZones(prev => [...prev, newZone]);
    setNewZoneIds(prev => new Set([...prev, tempId]));
    // Auto-select the new zone
    setEditingZoneId(tempId);
    // Auto-switch to Select mode so user can adjust
    setZoneMode('select');
  }, [placingFieldId, placingBusy, template, startOneBox]);

  // Zone delete: persists at once. Deleting a field's value zone also deletes
  // its anchor and region zones (the server decides; confirmed first, naming
  // what goes). An anchor, a region or one page's table zone goes alone, with
  // no confirmation. A zone drawn but never saved is just dropped locally.
  const handleZoneDelete = useCallback(async (zoneId) => {
    const zone = zones.find(z => z.id === zoneId);
    if (!zone) return;
    console.log('[TemplateEditor] Zone delete:', zoneId, zone.zone_type);

    const dropLocally = (ids) => {
      setZones(prev => prev.filter(z => !ids.has(z.id)));
      setDirtyZoneIds(prev => new Set([...prev].filter(id => !ids.has(id))));
      setNewZoneIds(prev => new Set([...prev].filter(id => !ids.has(id))));
      if (ids.has(editingZoneId)) setEditingZoneId(null);
    };

    if (newZoneIds.has(zoneId) || !template) {
      dropLocally(new Set([zoneId]));
      return;
    }

    const message = cascadeConfirmMessage(zone, zones, fields);
    if (message && !window.confirm(message)) return;

    try {
      const res = await deleteZone(template.id, zoneId);
      dropLocally(new Set(res?.deletedZoneIds || [zoneId]));
    } catch (err) {
      if (err?.response?.status !== 404) {
        console.error('[TemplateEditor] Zone delete failed:', err);
        setError(err?.response?.data?.error || err?.message || 'Failed to delete zone');
        return;
      }
      // Already gone (deleted in another tab): fall through and resync below.
      console.log('[TemplateEditor] Zone already deleted on the server:', zoneId);
    }

    // Resync: the hash (unpublished-changes banner), and drop any saved zone
    // the server no longer has. Local edits to the remaining zones are kept.
    try {
      const fresh = await getTemplateFull(template.id);
      if (fresh.ok) {
        const serverIds = new Set((fresh.zones || []).map(z => z.id));
        setZones(prev => prev.filter(z => serverIds.has(z.id) || String(z.id).startsWith('temp-')));
        setCurrentHash(fresh.currentHash || null);
      }
    } catch (err) {
      console.warn('[TemplateEditor] Re-fetch after zone delete failed:', err?.message || err);
    }
  }, [zones, fields, newZoneIds, editingZoneId, template]);

  // Remove several table zones (the Line Items "beyond page N" action): the
  // existing delete route, one zone per call, in order. Table zones never
  // cascade, so no confirmation per zone — LineItemsSection asked once. Stops
  // at the first failure; whatever was removed by then stays removed.
  const handleRemoveTableZones = useCallback(async (zoneIds) => {
    if (!template || zoneIds.length === 0) return;
    const removed = new Set();
    let failure = null;
    for (const zoneId of zoneIds) {
      try {
        const res = await deleteZone(template.id, zoneId);
        for (const id of (res?.deletedZoneIds || [zoneId])) removed.add(id);
      } catch (err) {
        if (err?.response?.status === 404) { removed.add(zoneId); continue; }
        failure = err;
        break;
      }
    }
    console.log(`[TemplateEditor] Removed ${removed.size} of ${zoneIds.length} table zone(s)${failure ? ' (stopped on error)' : ''}`);
    setZones(prev => prev.filter(z => !removed.has(z.id)));
    setDirtyZoneIds(prev => new Set([...prev].filter(id => !removed.has(id))));
    if (removed.has(editingZoneId)) setEditingZoneId(null);
    if (failure) {
      const reason = failure?.response?.data?.error || failure?.message || 'Failed to delete zone';
      setError(`Removed ${removed.size} of ${zoneIds.length} table zones, then stopped: ${reason}`);
    }
    try {
      const fresh = await getTemplateFull(template.id);
      if (fresh.ok) {
        const serverIds = new Set((fresh.zones || []).map(z => z.id));
        setZones(prev => prev.filter(z => serverIds.has(z.id) || String(z.id).startsWith('temp-')));
        setCurrentHash(fresh.currentHash || null);
      }
    } catch (err) {
      console.warn('[TemplateEditor] Re-fetch after table zone removal failed:', err?.message || err);
    }
  }, [template, editingZoneId]);

  // Save Template: persist all dirty/new/deleted zones, then touch template
  const handleSaveTemplate = useCallback(async () => {
    if (!template) return;
    setSaving(true);
    setSaveSuccess(false);
    setError(null);
    try {
      const writes = [];

      // 1. Create new zones (deletes already persisted in handleZoneDelete)
      for (const tempId of newZoneIds) {
        const z = zones.find(zz => zz.id === tempId);
        if (!z) continue;
        writes.push({ kind: 'create', id: tempId, run: () => createZone(template.id, {
          field_id: z.field_id,
          zone_type: z.zone_type,
          page_index: z.page_index ?? 0,
          x: z.x, y: z.y, w: z.w, h: z.h,
        }) });
      }

      // 2. Update dirty zones (existing, modified)
      for (const zoneId of dirtyZoneIds) {
        const z = zones.find(zz => zz.id === zoneId);
        if (!z) continue;
        writes.push({ kind: 'update', id: zoneId, run: () => updateZone(template.id, zoneId, { x: z.x, y: z.y, w: z.w, h: z.h, field_id: z.field_id || null }) });
      }

      const results = await Promise.allSettled(writes.map(w => w.run()));
      const failed = results.map((r, i) => (r.status === 'rejected' ? { write: writes[i], err: r.reason } : null)).filter(Boolean);
      if (failed.length > 0) {
        // The server refuses a zone it cannot use (an anchor box with no label
        // under it: 422). The zones that did persist leave the pending sets so
        // a retry does not create them twice; the refused ones stay pending.
        const created = new Map();
        results.forEach((r, i) => {
          const savedZone = r.status === 'fulfilled' ? (r.value?.zone || r.value) : null;
          if (writes[i].kind === 'create' && savedZone?.id) created.set(writes[i].id, savedZone);
        });
        const settled = new Set(writes.filter((w, i) => results[i].status === 'fulfilled').map(w => w.id));
        setZones(prev => prev.map(z => created.get(z.id) || z));
        setNewZoneIds(prev => new Set([...prev].filter(id => !settled.has(id))));
        setDirtyZoneIds(prev => new Set([...prev].filter(id => !settled.has(id))));
        const first = failed[0].err;
        const reason = first?.response?.data?.error || first?.message || 'Failed to save zone';
        setError(failed.length === 1 ? reason : `${failed.length} zones were not saved. ${reason}`);
        return;
      }

      // 3. Touch template updated_at
      await saveTemplate(template.id);

      // 4. Re-fetch to confirm
      const fresh = await getTemplateFull(template.id);
      if (fresh.ok) {
        setTemplate(fresh.template);
        setFields(fresh.fields || []);
        setZones(fresh.zones || []);
        setDirtyZoneIds(new Set());
        setNewZoneIds(new Set());
        setEditingZoneId(null);
        setCurrentHash(fresh.currentHash || null);
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      console.log('[TemplateEditor] Template saved successfully');
      if (onSave) onSave();
    } catch (err) {
      console.error('[TemplateEditor] Save failed:', err);
      setError(err?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  }, [template, zones, dirtyZoneIds, newZoneIds, onSave]);

  // Discard Changes: reload from server
  const handleDiscardChanges = useCallback(async () => {
    if (!template) return;
    if (!window.confirm('Discard all unsaved zone changes and reload from the last saved state?')) return;
    setLoading(true);
    try {
      const response = await getTemplateFull(template.id);
      if (response.ok) {
        setTemplate(response.template);
        setFields(response.fields || []);
        setZones(response.zones || []);
        setRegions(response.regions || []);
        setDirtyZoneIds(new Set());
        setNewZoneIds(new Set());
        setEditingZoneId(null);
        setSelectedField(null);
      }
    } catch (err) {
      setError(err?.message || 'Failed to reload template');
    } finally {
      setLoading(false);
    }
  }, [template]);

  // AI Refine Zone: request AI-proposed zone for selected field
  const handleAiRefine = useCallback(async () => {
    const sf = selectedField || (editingZoneId ? fields.find(f => {
      const z = zones.find(zz => zz.id === editingZoneId);
      return z && f.id === z.field_id;
    }) : null);
    if (!sf || !template) return;
    setAiRefineLoading(true);
    setAiRefineError(null);
    setAiRefineProposal(null);
    try {
      const url = `/api/capture/templates/${template.id}/fields/${sf.id}/ai-refine-zone`;
      const resp = await captureAPI.postJson(url, { user_hint: aiRefineHint || undefined });
      setAiRefineProposal(resp);
    } catch (err) {
      setAiRefineError(err?.response?.data?.error || err?.message || 'AI refinement failed');
    } finally {
      setAiRefineLoading(false);
    }
  }, [selectedField, editingZoneId, fields, zones, template, aiRefineHint]);

  // Accept AI proposal: apply to local zone state
  const handleAcceptAiProposal = useCallback(() => {
    if (!aiRefineProposal?.proposedZone) return;
    const sf = selectedField || (editingZoneId ? fields.find(f => {
      const z = zones.find(zz => zz.id === editingZoneId);
      return z && f.id === z.field_id;
    }) : null);
    if (!sf) return;
    const { proposedZone } = aiRefineProposal;
    const existingZone = zones.find(z => z.field_id === sf.id && z.zone_type === 'field');

    if (existingZone) {
      setZones(prev => prev.map(z =>
        z.id === existingZone.id
          ? { ...z, x: proposedZone.x, y: proposedZone.y, w: proposedZone.w, h: proposedZone.h, page_index: proposedZone.page_index }
          : z
      ));
      setDirtyZoneIds(prev => new Set([...prev, existingZone.id]));
    } else {
      const tempId = `temp_ai_${Date.now()}`;
      setZones(prev => [...prev, {
        id: tempId,
        field_id: sf.id,
        zone_type: 'field',
        x: proposedZone.x,
        y: proposedZone.y,
        w: proposedZone.w,
        h: proposedZone.h,
        page_index: proposedZone.page_index,
      }]);
      setNewZoneIds(prev => new Set([...prev, tempId]));
    }

    setAiRefineProposal(null);
    setAiRefineHint('');
    setShowAiRefinePanel(false);
  }, [aiRefineProposal, selectedField, editingZoneId, fields, zones]);

  // Publish handler
  const handlePublish = useCallback(async () => {
    if (!template) return;
    setPublishing(true);
    setError(null);
    try {
      const result = await publishTemplate(template.id);
      if (result.ok) {
        // Re-fetch to get updated template with publish fields
        const fresh = await getTemplateFull(template.id);
        if (fresh.ok) {
          setTemplate(fresh.template);
          setFields(fresh.fields || []);
          setZones(fresh.zones || []);
          setCurrentHash(fresh.currentHash || null);
        }
        console.log(`[TemplateEditor] Published v${result.publishedVersion}`);
      }
    } catch (err) {
      console.error('[TemplateEditor] Publish failed:', err);
      setError(err?.message || 'Failed to publish template');
    } finally {
      setPublishing(false);
    }
  }, [template]);

  // Unpublish handler
  const handleUnpublish = useCallback(async () => {
    if (!template) return;
    setPublishing(true);
    setError(null);
    try {
      const result = await unpublishTemplate(template.id);
      if (result.ok) {
        const fresh = await getTemplateFull(template.id);
        if (fresh.ok) {
          setTemplate(fresh.template);
          setFields(fresh.fields || []);
          setZones(fresh.zones || []);
          setCurrentHash(fresh.currentHash || null);
        }
        console.log('[TemplateEditor] Unpublished');
      }
    } catch (err) {
      console.error('[TemplateEditor] Unpublish failed:', err);
      setError(err?.message || 'Failed to unpublish template');
    } finally {
      setPublishing(false);
    }
  }, [template]);

  // Derived publish status (prefer status column, fallback to published_at)
  const isPublished = template?.status === 'published' || Boolean(template?.published_at);
  const hasUnpublishedChanges = isPublished && currentHash && template?.published_hash && currentHash !== template.published_hash;

  // Zone-to-field assignment: update local state + persist immediately for saved zones
  // field_id is NOT NULL — only accept a valid field ID
  const handleZoneFieldAssign = useCallback(async (zoneId, fieldId) => {
    if (!fieldId) return; // field_id is required
    // Update local state
    setZones(prev => prev.map(z => z.id === zoneId ? { ...z, field_id: fieldId } : z));
    // If it's a persisted zone (not temp), patch immediately
    if (!newZoneIds.has(zoneId) && template) {
      try {
        await patchZone(template.id, zoneId, { field_id: fieldId });
        console.log(`[TemplateEditor] Zone ${zoneId} assigned to field ${fieldId}`);
      } catch (err) {
        console.error('[TemplateEditor] Zone field assign failed:', err);
        setError(err?.message || 'Failed to assign field');
      }
    }
  }, [template, newZoneIds]);

  // Update zone anchor match settings (persists immediately via PUT)
  const handleZoneAnchorUpdate = useCallback(async (zoneId, updates) => {
    if (!template) return false;
    try {
      const result = await updateZone(template.id, zoneId, updates);
      if (result.ok && result.zone) {
        // Update local state with the returned zone
        setZones(prev => prev.map(z => z.id === zoneId ? { ...z, ...result.zone } : z));
        console.log(`[TemplateEditor] Zone anchor settings updated id=${zoneId}`);
        return { ok: true, zone: result.zone };
      }
      // Backend responded but didn't confirm the write — surface the error and
      // report failure so the panel keeps its dirty state (no false success).
      const error = result?.error || 'Failed to update anchor settings';
      setError(error);
      return { ok: false, error };
    } catch (err) {
      console.error('[TemplateEditor] Zone anchor update failed:', err);
      const error = err?.response?.data?.error || err?.message || 'Failed to update anchor settings';
      setError(error);
      return { ok: false, error };
    }
  }, [template]);

  // Find the selected zone object
  const selectedZone = editingZoneId ? zones.find(z => z.id === editingZoneId) : null;
  const selectedZoneField = selectedZone ? fields.find(f => f.id === selectedZone.field_id) : null;

  // Zone text preview for selected zone (memoized)
  // field_key → friendly label map for the Test Run panel (clean Library-like rows).
  const fieldLabels = useMemo(
    () => Object.fromEntries(fields.map(f => [f.field_key, f.label || f.field_key])),
    [fields]
  );

  const zoneTextPreview = useMemo(() => {
    if (!selectedZone) return null;
    const pageIndex = selectedZone.page_index ?? 0;
    const currentPageIndex = (currentPageNumber || 1) - 1;
    if (pageIndex !== currentPageIndex) {
      return { text: '', wordCount: 0, available: false, wrongPage: true };
    }
    return computeZoneTextPreview(selectedZone, ocrWords);
  }, [selectedZone, ocrWords, currentPageNumber]);

  // Field provenance (display-only, Option A): the doc-type's canonical library
  // selection, derived once from the already-loaded fieldLibrary. Lifted to
  // component scope so BOTH the flat field list and the region-grouped editor
  // (RegionList, used by AI-built templates) can share it. Filter by doc_type
  // ONLY — association existence is what counts; a disabled association
  // (default_enabled=false) is still library-selected.
  const librarySelectedKeys = useMemo(
    () => new Set(
      (fieldLibrary || [])
        .filter(li => li.doc_type === template?.document_type)
        .map(li => li.field_key)
    ),
    [fieldLibrary, template?.document_type]
  );

  // Field Library panel is scoped to the current template's doc type, so a field
  // associated with multiple doc types no longer repeats. Falls back to a
  // field_key-deduped list when the template has no doc type yet.
  const scopedFieldLibrary = useMemo(() => {
    const docType = template?.document_type;
    if (docType) return fieldLibrary.filter(li => li.doc_type === docType);
    const seen = new Set();
    return fieldLibrary.filter(li => {
      if (seen.has(li.field_key)) return false;
      seen.add(li.field_key);
      return true;
    });
  }, [fieldLibrary, template?.document_type]);

  // The field list (flat and by region) and the Draw field picker leave
  // document attributes out. They are not deleted or changed;
  // DocumentAttributesSection mentions them.
  const listFields = useMemo(
    () => fields.filter(f => !isDocumentAttributeKey(f.field_key)),
    [fields]
  );

  const scopedFieldLibraryCategories = useMemo(
    () => [...new Set(scopedFieldLibrary.map(li => li.category))],
    [scopedFieldLibrary]
  );

  if (loading) {
    return (
      <div className="template-editor-page">
        <div className="editor-header">
          <div className="header-left">
            <button onClick={onBack} className="btn btn-secondary">
              ← Back to Templates
            </button>
            <div className="template-info">
              <h1>Loading Template...</h1>
            </div>
          </div>
        </div>
        <div className="editor-content">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  if (error && !template) {
    return (
      <div className="template-editor-page">
        <div className="editor-header">
          <div className="header-left">
            <button onClick={onBack} className="btn btn-secondary">
              ← Back to Templates
            </button>
            <div className="template-info">
              <h1>Error Loading Template</h1>
            </div>
          </div>
        </div>
        <div className="editor-content">
          <div className="error-message">
            <p>Failed to load template: {error}</p>
            <button onClick={loadTemplate} className="btn btn-primary">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="template-editor-page">
      <EditorHeader
        template={template}
        onBack={onBack}
        editingName={editingName}
        nameValue={nameValue}
        nameSaving={nameSaving}
        onNameChange={setNameValue}
        onNameEditStart={() => { setEditingName(true); setNameValue(template?.name || ''); }}
        onNameEditCancel={() => setEditingName(false)}
        onNameSubmit={async () => {
          const trimmed = nameValue.trim();
          if (!trimmed || trimmed === template?.name) { setEditingName(false); return; }
          setNameSaving(true);
          try {
            await saveTemplate(template.id, { name: trimmed });
            setTemplate(prev => ({ ...prev, name: trimmed }));
            setEditingName(false);
          } catch (err) {
            setError(err?.response?.data?.error || err?.message || 'Failed to rename template');
          } finally { setNameSaving(false); }
        }}
        showOcrWords={showOcrWords}
        showRegions={showRegions}
        showAdvanced={showAdvanced}
        showZoneTypeField={showZoneTypeField}
        showZoneTypeAnchor={showZoneTypeAnchor}
        showZoneTypeRegion={showZoneTypeRegion}
        showZoneTypeTable={showZoneTypeTable}
        showAnchorRegion={showAnchorRegion}
        onToggleAnchorRegion={() => setShowAnchorRegion(v => !v)}
        onToggleOcrWords={() => setShowOcrWords(!showOcrWords)}
        onToggleRegions={() => setShowRegions(!showRegions)}
        onToggleAdvanced={() => setShowAdvanced(!showAdvanced)}
        onToggleZoneTypeField={() => setShowZoneTypeField(!showZoneTypeField)}
        onToggleZoneTypeAnchor={() => setShowZoneTypeAnchor(!showZoneTypeAnchor)}
        onToggleZoneTypeRegion={() => setShowZoneTypeRegion(!showZoneTypeRegion)}
        onToggleZoneTypeTable={() => setShowZoneTypeTable(v => !v)}
        onTestRun={handleTestRun}
        testRunLoading={testRunLoading}
        onDetectRegions={handleDetectRegions}
        detectingRegions={detectingRegions}
        onSave={handleSaveTemplate}
        saving={saving}
        isDirty={isDirty}
        onDiscard={handleDiscardChanges}
        onPublish={handlePublish}
        onUnpublish={handleUnpublish}
        publishing={publishing}
        isPublished={isPublished}
        hasUnpublishedChanges={hasUnpublishedChanges}
        canBuild={canBuild}
        docTypeSelector={
          <DocTypeSelector
            value={template?.document_type || ''}
            showTypeMeta
            compactMeta={narrowHeader}
            onChange={async (key) => {
              if (!template || !key || key === template.document_type) return;
              try {
                await saveTemplate(template.id, { document_type: key });
                setTemplate(prev => (prev ? { ...prev, document_type: key } : prev));
              } catch (err) {
                setError(err?.response?.data?.error || err?.message || 'Failed to update document type');
              }
            }}
          />
        }
      />

      {/* Unpublished changes banner — Publish is a builder action (B-2). */}
      {hasUnpublishedChanges && (
        <div className="unpublished-banner">
          <span>You have unpublished changes since v{template?.published_version}.</span>
          {canBuild && (
            <button
              onClick={handlePublish}
              disabled={publishing || isDirty}
              className="btn btn-sm btn-publish"
            >
              {publishing ? 'Publishing…' : 'Publish Now'}
            </button>
          )}
        </div>
      )}

      {/* Identifier word-pick banner */}
      {identifierPickMode && (
        <div className="place-zone-banner" style={{ background: '#0f766e', borderColor: '#0d9488' }}>
          <span>
            🔍 <strong>Click any word on the document</strong> to use it as identifier text. Press Esc to cancel.
          </span>
          <button
            className="btn btn-sm btn-cancel-place"
            onClick={() => setIdentifierPickMode(false)}
          >
            Cancel
          </button>
        </div>
      )}

      {/* One-box confirmation — replaces the Place Zone banner while a label is shown */}
      {oneBox && (
        <OneBoxConfirm
          fieldLabel={oneBox.fieldLabel}
          status={oneBox.status}
          preview={oneBox.preview}
          error={oneBox.error}
          choice={oneBox.choice}
          typed={oneBox.typed}
          noLabel={oneBox.noLabel}
          positionOnly={oneBox.positionOnly}
          replacing={placedZonesOf(oneBox.fieldId).length}
          onChoose={(text) => setOneBox(prev => prev && ({ ...prev, choice: text, typed: '' }))}
          onTypedChange={(text) => setOneBox(prev => prev && ({ ...prev, typed: text }))}
          onPositionOnlyChange={(checked) => setOneBox(prev => prev && ({ ...prev, positionOnly: checked, typed: checked ? '' : prev.typed }))}
          onUse={handleOneBoxUse}
          onCancel={cancelOneBox}
        />
      )}

      {/* Place Zone banner — shown when in placement mode for a field */}
      {isPlacing && !oneBox && (
        <div className="place-zone-banner">
          {placingBusy ? (
            <span>Drawing zone for <strong>{placingFieldLabel}</strong>…</span>
          ) : fieldPickMode ? (
            <span>
              📍 Placing: <strong>{placingFieldLabel}</strong> —
              <strong> Click a word</strong> or drag over several words on the document, or
              <button
                className="btn btn-sm"
                style={{ marginLeft: '8px', marginRight: '4px', background: 'var(--stm-bg-surface)', border: '1px solid var(--stm-accent-info)', color: 'var(--stm-accent-info)', padding: '1px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                onClick={() => setFieldPickMode(false)}
              >
                Draw a region
              </button>
              instead. Press Esc to cancel.
            </span>
          ) : (
            <span>
              ✏️ Drawing zone for: <strong>{placingFieldLabel}</strong> — drag on the document, or
              <button
                className="btn btn-sm"
                style={{ marginLeft: '8px', marginRight: '4px', background: 'var(--stm-bg-surface)', border: '1px solid var(--stm-accent-info)', color: 'var(--stm-accent-info)', padding: '1px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                onClick={() => setFieldPickMode(true)}
              >
                Click a word
              </button>
              instead. Press Esc to cancel.
            </span>
          )}
          <button className="btn btn-sm btn-cancel-place" onClick={cancelPlaceZone} disabled={placingBusy}>Cancel</button>
        </div>
      )}

      {/* Main Content */}
      <div className="editor-layout template-studio-v2-layout">
        {/* Left Panel - Regions + Fields + Identifiers */}
        <div className="fields-panel">
          {/* Tab bar */}
          <div className="left-panel-tabs">
            <button
              className={`left-panel-tab ${leftTab === 'fields' ? 'active' : ''}`}
              onClick={() => setLeftTab('fields')}
            >
              Fields
            </button>
            <button
              className={`left-panel-tab ${leftTab === 'identifiers' ? 'active' : ''}`}
              onClick={() => setLeftTab('identifiers')}
            >
              Identifiers
            </button>
          </div>

          {leftTab === 'identifiers' ? (
            <TemplateIdentifiers
              templateId={templateId}
              onRequestPickWord={viewerDocument ? () => setIdentifierPickMode(true) : null}
              pendingPrefillValue={identifierPrefillValue}
              onPrefillConsumed={() => setIdentifierPrefillValue('')}
            />
          ) : (
          <>
          <div className="panel-header">
            <h3>{fieldGrouping === 'grouped' ? 'Template Regions' : 'Template Fields'}</h3>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {/* View toggle: Flat field list (default) vs grouped by region.
                  "By region" runs Organize first if no regions exist yet. */}
              <div style={{ display: 'inline-flex', border: '1px solid #d1d5db', borderRadius: '6px', overflow: 'hidden' }}>
                <button
                  type="button"
                  onClick={() => setFieldGrouping('flat')}
                  title="Show all fields as a flat list"
                  style={{
                    border: 'none', borderRadius: 0, padding: '2px 10px', fontSize: '12px', cursor: 'pointer',
                    background: fieldGrouping === 'flat' ? 'var(--stm-accent-info, #3b82f6)' : '#fff',
                    color: fieldGrouping === 'flat' ? '#fff' : '#374151',
                    fontWeight: fieldGrouping === 'flat' ? 600 : 400,
                  }}
                >
                  Flat
                </button>
                <button
                  type="button"
                  onClick={handleSelectByRegion}
                  disabled={detectingRegions}
                  title={regions.length > 0 ? 'Group fields by document region' : 'Group fields into regions (runs Organize)'}
                  style={{
                    border: 'none', borderLeft: '1px solid #d1d5db', borderRadius: 0, padding: '2px 10px', fontSize: '12px', cursor: 'pointer',
                    background: fieldGrouping === 'grouped' ? 'var(--stm-accent-info, #3b82f6)' : '#fff',
                    color: fieldGrouping === 'grouped' ? '#fff' : '#374151',
                    fontWeight: fieldGrouping === 'grouped' ? 600 : 400,
                  }}
                >
                  {detectingRegions ? '…' : 'By region'}
                </button>
              </div>
              <button
                onClick={() => setShowFieldForm(true)}
                className="btn btn-primary btn-sm"
              >
                Add Field
              </button>
            </div>
          </div>
          {detectRegionsError && (
            <div style={{ padding: '6px 10px', background: '#fef2f2', borderBottom: '1px solid #fee2e2', fontSize: '11px', color: '#dc2626' }}>
              {detectRegionsError}
              <button onClick={() => setDetectRegionsError(null)} style={{ marginLeft: '8px', background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '11px' }}>✕</button>
            </div>
          )}
          <DocumentAttributesSection
            documentType={template?.document_type || ''}
            attributeField={fields.find(f => isDocumentAttributeKey(f.field_key)) || null}
          />
          {(fieldGrouping === 'grouped' && regions.length > 0) ? (
            <RegionList
              regions={regions}
              fields={listFields}
              selectedRegion={selectedRegion}
              selectedField={selectedField}
              onSelectRegion={(region) => {
                setSelectedRegion(region);
                setSelectedField(null);
                setEditingZoneId(null);
              }}
              onSelectField={(field) => {
                setSelectedField(field);
                setSelectedRegion(null);
                setEditingZoneId(null);
              }}
              onAddField={(regionId) => {
                console.log('[TemplateEditor] Add field to region:', regionId);
                setShowFieldForm(true);
              }}
              getRegionColor={getRegionColor}
              onFieldRegionChange={handleFieldRegionChange}
              librarySelectedKeys={librarySelectedKeys}
              zones={zones}
              onToggleEnabled={handleFieldToggleEnabled}
              onPlaceZone={startPlaceZone}
              onEditField={handleFieldEdit}
              onDeleteField={handleFieldRowDelete}
            />
          ) : (
          <>
          <div className="fields-list">
            {(() => {
              const nonLiFields = listFields.filter(f => !f.is_line_items);
              const zonedFieldIds = new Set(zones.filter(z => z.field_id).map(z => z.field_id));
              const unzonedCount = nonLiFields.filter(f => !zonedFieldIds.has(f.id)).length;
              const displayFields = showUnzonedFields
                ? nonLiFields
                : nonLiFields.filter(f => zonedFieldIds.has(f.id));

              // librarySelectedKeys is derived once at component scope (shared
              // with the region-grouped editor); see the useMemo above.
              return nonLiFields.length > 0 ? (
                <>
                  {unzonedCount > 0 && (
                    <div className="unzoned-toggle" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 8px', fontSize: '11px', color: '#9ca3af', borderBottom: '1px solid #f3f4f6' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={showUnzonedFields}
                          onChange={() => setShowUnzonedFields(!showUnzonedFields)}
                          style={{ width: '12px', height: '12px' }}
                        />
                        Show unzoned fields ({unzonedCount})
                      </label>
                    </div>
                  )}
                  {displayFields.map(field => {
                    const fieldZones = zones.filter(z => z.field_id === field.id);
                    const isSelected = selectedField?.id === field.id;
                    const isEnabled = field.enabled !== false;
                    return (
                      <FieldRow
                        key={field.id}
                        field={field}
                        isSelected={isSelected}
                        isEnabled={isEnabled}
                        fieldZones={fieldZones}
                        librarySelectedKeys={librarySelectedKeys}
                        onSelect={() => setSelectedField(isSelected ? null : field)}
                        onToggleEnabled={handleFieldToggleEnabled}
                        onPlaceZone={startPlaceZone}
                        onEdit={handleFieldEdit}
                        onDelete={handleFieldRowDelete}
                      />
                    );
                  })}
                </>
              ) : (
                <div className="empty-state">
                  <p>No fields defined yet.</p>
                  <button 
                    onClick={() => setShowFieldForm(true)}
                    className="btn btn-primary"
                  >
                    Add First Field
                  </button>
                </div>
              );
            })()}
          </div>
          {/* Line Items Section — one summary row; the per-page list is a disclosure. */}
          <LineItemsSection
            tableZones={zones.filter(z => z.zone_type === 'line_items_table')}
            samplePageCount={sampleDocPageCount}
            onSelectZone={(tableZone) => {
              setCurrentPageNumber((tableZone.page_index ?? 0) + 1);
              setEditingZoneId(tableZone.id);
            }}
            onDeleteZone={handleZoneDelete}
            onRemoveZones={handleRemoveTableZones}
            onDrawTableZone={() => {
              setDrawZoneType('line_items_table');
              setZoneMode('draw');
            }}
          />

          </>
          )}
          </>
          )}

          <FieldLibraryPanel
            fieldLibrary={scopedFieldLibrary}
            fieldLibraryCategories={scopedFieldLibraryCategories}
            fields={fields}
            onAddField={handleLibraryAdd}
            addingKey={libAddingKey}
          />
        </div>

        {/* Center Panel - PDF Viewer */}
        <div className="viewer-panel">
          <div className="panel-header">
            <h3>Document Viewer</h3>
            {viewerDocument && (
              <div className="overlay-controls">
                {/* Mode toggle: Select / Draw */}
                <div className="mode-toggle">
                  <button
                    type="button"
                    className={`btn-mode ${zoneMode === 'select' ? 'active' : ''}`}
                    onClick={() => setZoneMode('select')}
                    title="Select mode: click zones to select, drag to move, corners to resize"
                  >
                    Select
                  </button>
                  <button
                    type="button"
                    className={`btn-mode ${zoneMode === 'draw' ? 'active' : ''}`}
                    onClick={() => {
                      setZoneMode('draw');
                      // Auto-select first non-line-items field if none selected
                      const nonLiFields = listFields.filter(f => !f.is_line_items);
                      if (!drawFieldId && nonLiFields.length > 0) {
                        setDrawFieldId(nonLiFields[0].id);
                      }
                      // If no fields at all, default to line_items_table mode
                      if (nonLiFields.length === 0) {
                        setDrawZoneType('line_items_table');
                      }
                    }}
                    title="Draw mode: click+drag on PDF to create a new zone"
                  >
                    Draw
                  </button>
                </div>

                {/* Draw mode options */}
                {zoneMode === 'draw' && (
                  <div className="draw-options">
                    <select
                      value={drawZoneType}
                      onChange={(e) => setDrawZoneType(e.target.value)}
                      className="draw-select"
                      title="Zone type for new zones"
                    >
                      <option value="field">Field Zone</option>
                      <option value="line_items_table">Line Items Table</option>
                      {showAdvanced && (
                        <>
                          <option value="anchor">Anchor Zone</option>
                          <option value="region">Region Zone</option>
                        </>
                      )}
                    </select>
                    {drawZoneType !== 'line_items_table' && (
                      <select
                        value={drawFieldId || ''}
                        onChange={(e) => setDrawFieldId(e.target.value || null)}
                        className="draw-select draw-select-field"
                        title="Active field — zone will be assigned to this field"
                      >
                        {listFields.filter(f => !f.is_line_items).map(f => (
                          <option key={f.id} value={f.id}>{f.label || f.field_key}</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                <button
                  type="button"
                  className={`btn-overlay-toggle ${showOcrWords ? 'active' : ''}`}
                  onClick={() => setShowOcrWords(!showOcrWords)}
                  title={showOcrWords ? 'Hide OCR words' : 'Show OCR words'}
                >
                  OCR
                </button>
              </div>
            )}
          </div>
          
          {viewerDocument ? (
            <div className="pdf-viewer-wrapper" style={{ position: 'relative' }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => sampleFileInputRef.current?.click()}
                disabled={uploadingDoc}
                title="Replace the sample document used for zone drawing and test runs"
                style={{
                  position: 'absolute', top: '8px', right: '8px', zIndex: 20,
                  fontSize: '11px', padding: '3px 8px', opacity: 0.85,
                }}
              >
                {uploadingDoc ? 'Uploading…' : '↺ Replace Doc'}
              </button>
              <TemplateZoneViewer
                document={viewerDocument}
                onNumPagesChange={handleViewerNumPages}
                currentPageNumber={currentPageNumber}
                onPageChange={setCurrentPageNumber}
                renderPageOverlay={(pageNumber, { isActive }) => {
                  // Read-only zone set for THIS page (inactive pages only). Same
                  // visibleZones the active page uses, filtered to this page —
                  // matches ZoneEditorOverlay's internal page filter so the same
                  // zones show. page_index is 0-based; pageNumber is 1-based.
                  const pageZones = visibleZones.filter(
                    z => (z.page_index ?? 0) === pageNumber - 1
                  );
                  return (
                    <>
                      {/* Region Overlay — rendered below OCR words and zones.
                          Active page keeps the existing interactive overlay
                          unchanged; inactive pages get a display-only overlay
                          filtered to this page (regions carry page_number,
                          0-based). */}
                      {isActive ? (
                        <RegionOverlay
                          regions={regions.filter(
                            r => (r.page_number ?? 0) === pageNumber - 1
                          )}
                          selectedRegion={selectedRegion}
                          hoveredRegion={hoveredRegion}
                          onRegionClick={(region) => {
                            setSelectedRegion(region);
                            setSelectedField(null);
                            setEditingZoneId(null);
                          }}
                          onRegionHover={setHoveredRegion}
                          showRegions={showRegions}
                          getRegionColor={getRegionColor}
                        />
                      ) : (
                        <RegionOverlay
                          regions={regions.filter(
                            r => (r.page_number ?? 0) === pageNumber - 1
                          )}
                          selectedRegion={null}
                          hoveredRegion={null}
                          showRegions={showRegions}
                          getRegionColor={getRegionColor}
                          readOnly
                        />
                      )}

                      {/* OCR Word Overlay — kept STABLE per page (same element,
                          same fixed currentPageNumber={pageNumber}) so scrolling
                          never refetches/evicts OCR. Interactivity toggles by
                          isActive: inactive pages pass no click/index handlers,
                          so their boxes become pointer-events:none. */}
                      <OcrWordOverlay
                        documentId={viewerDocument?.id}
                        currentPageNumber={pageNumber}
                        showOverlay={isActive
                          ? (showOcrWords || identifierPickMode || fieldPickMode)
                          : showOcrWords}
                        /* no engine filter: see OcrWordOverlay — filtering to paddle rendered
                         an empty overlay for every text-layer-acquired document */
                        clickToIndex={isActive ? identifierPickMode : false}
                        onWordClick={isActive ? (
                          identifierPickMode ? (word) => {
                            setIdentifierPrefillValue(word.text);
                            setIdentifierPickMode(false);
                          } : fieldPickMode ? (word) => handleFieldWordClick(word, pageNumber - 1)
                          : undefined
                        ) : undefined}
                      />

                      {/* Zone layer — the ONLY layer that swaps by active state.
                          Active: interactive ZoneEditorOverlay. In a pick mode
                          (Click a word, identifier pick) neither the overlay nor
                          any zone box takes the pointer, so a click on a word
                          inside a zone reaches the OcrWordOverlay beneath; every
                          zone but the placed field's is dimmed. Inactive:
                          static, display-only ReadOnlyZoneLayer. */}
                      {isActive ? (
                        <ZoneEditorOverlay
                          selectedField={selectedField}
                          zones={visibleZones}
                          onZoneCommit={handleZoneCommit}
                          onZoneDelete={handleZoneDelete}
                          onZoneCreate={handleZoneCreate}
                          currentPageNumber={currentPageNumber}
                          fields={fields}
                          onSelectField={setSelectedField}
                          editingZoneId={editingZoneId}
                          onEditingZoneIdChanged={setEditingZoneId}
                          mode={isPlacing && !fieldPickMode ? 'draw' : zoneMode}
                          drawZoneType={isPlacing ? 'field' : drawZoneType}
                          drawFieldId={isPlacing ? placingFieldId : drawFieldId}
                          ocrWords={wordsByPage[pageNumber - 1] ?? null}
                          loadWords={loadWordsForPage}
                          onModeSwitch={(m) => { if (!isPlacing) setZoneMode(m); }}
                          passthroughPointerEvents={fieldPickMode || identifierPickMode}
                          dragToDraw={fieldPickMode}
                          dimAnchorRegion={!showAnchorRegion}
                          pickMode={fieldPickMode || identifierPickMode}
                          pickFieldId={fieldPickMode ? placingFieldId : null}
                        />
                      ) : (
                        <ReadOnlyZoneLayer
                          zones={pageZones}
                          fields={fields}
                          dimAnchorRegion={!showAnchorRegion}
                          selectedFieldId={selectedField?.id ?? null}
                          pickMode={fieldPickMode || identifierPickMode}
                          pickFieldId={fieldPickMode ? placingFieldId : null}
                        />
                      )}
                    </>
                  );
                }}
              />
            </div>
          ) : (
            <div className="no-document">
              <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>📄</div>
              <p style={{ fontWeight: 600, marginBottom: '6px' }}>
                {template && !template.source_document_id
                  ? 'No sample document linked'
                  : 'Could not load document preview'}
              </p>
              <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px', maxWidth: '320px', textAlign: 'center', lineHeight: 1.5 }}>
                Zone drawing requires a document to be displayed. Upload a sample document to enable
                the <strong>Select / Draw</strong> mode controls and start placing field zones.
              </p>
              <button className="btn btn-primary" onClick={() => sampleFileInputRef.current?.click()} disabled={uploadingDoc}>
                {uploadingDoc ? 'Uploading…' : '↑ Upload Sample Document'}
              </button>
              <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '10px' }}>
                Accepted: PDF files only
              </p>
            </div>
          )}
          {/* Always-present file input — used by both Upload and Replace Doc buttons */}
          <input ref={sampleFileInputRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={handleSampleDocumentUpload} />
        </div>

        {/* Right Panel - Properties & Field Mapping */}
        <div className="properties-panel">
          <div className="panel-header">
            <h3>{showTestRun ? 'Test Results' : 'Properties'}</h3>
            {!showTestRun && testRunResult && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setShowTestRun(true)}
                title="Return to the last test run (no re-run)"
              >
                ← Test Results
              </button>
            )}
          </div>
          <div className="properties-content">
            {/* ── Test Run Results ── */}
            {showTestRun ? (
              <>
                <TestRunPanel
                  result={testRunResult}
                  loading={testRunLoading}
                  error={testRunError}
                  fieldLabels={fieldLabels}
                  onClose={() => { setShowTestRun(false); setTestRunResult(null); setTestRunError(null); }}
                  onFieldClick={handleTestRunFieldClick}
                  onRerun={handleTestRun}
                />
                {/* Labeled separator introducing the assistant below the results.
                    Lightweight — the panel renders its own "Assistant" header. */}
                <div className="assistant-intro-band">
                  <span className="assistant-intro-icon" aria-hidden="true">✨</span>
                  <span className="assistant-intro-text">
                    Ask about these results or request a fix.
                  </span>
                </div>
                {/* AI-assist: interactive advisory chat + human-gated apply.
                    Op-aware callback: only mark_field_not_applicable changes
                    `required`; set_anchor / configure_transform must NOT touch it. */}
                <TemplateStudioAssistantPanel
                  templateId={templateId}
                  documentId={viewerDocument?.id}
                  fieldLabels={fieldLabels}
                  onFieldApplied={(fieldKey, after, op) => {
                    if (op === 'mark_field_not_applicable') {
                      setFields(prev => prev.map(f =>
                        f.field_key === fieldKey ? { ...f, required: after?.required ?? false } : f
                      ));
                      console.log(`[TemplateEditor] Field applied not-applicable: ${fieldKey} required=${after?.required}`);
                    } else {
                      console.log(`[TemplateEditor] Assistant applied ${op} to ${fieldKey}`);
                    }
                  }}
                />
              </>
            ) : selectedZone ? (
              <ZonePropertiesPanel
                selectedZone={selectedZone}
                selectedZoneField={selectedZoneField}
                zoneTextPreview={zoneTextPreview}
                fields={fields}
                zones={zones}
                showAdvanced={showAdvanced}
                showAiRefinePanel={showAiRefinePanel}
                aiRefineHint={aiRefineHint}
                aiRefineLoading={aiRefineLoading}
                aiRefineError={aiRefineError}
                aiRefineProposal={aiRefineProposal}
                onToggleAiRefinePanel={() => setShowAiRefinePanel(v => !v)}
                onAiRefineHintChange={setAiRefineHint}
                onAiRefine={handleAiRefine}
                onAcceptAiProposal={handleAcceptAiProposal}
                onDiscardAiProposal={() => setAiRefineProposal(null)}
                onZoneFieldAssign={handleZoneFieldAssign}
                onZoneAnchorUpdate={handleZoneAnchorUpdate}
                onZoneDelete={handleZoneDelete}
                onShowFieldForm={() => setShowFieldForm(true)}
                onSetIdentifierPrefill={setIdentifierPrefillValue}
                onSetLeftTab={setLeftTab}
                template={template}
              />

            ) : selectedField ? (
              /* ── Selected Field: redesigned properties panel ── */
              <FieldPropertiesPanel
                field={selectedField}
                zones={zones}
                ocrWords={ocrWords}
                computeZoneText={computeZoneTextPreview}
                onZoneAnchorUpdate={handleZoneAnchorUpdate}
                onZoneDelete={handleZoneDelete}
                onFieldDelete={handleFieldDelete}
                showAdvanced={showAdvanced}
                templateId={templateId}
                documentId={viewerDocument?.id}
                onAnchorReplaced={async () => {
                  const fresh = await getTemplateFull(template.id);
                  if (fresh.ok) {
                    setZones(fresh.zones || []);
                    setFields(fresh.fields || []);
                    setCurrentHash(fresh.currentHash || null);
                  }
                }}
              />

            ) : selectedRegion ? (
              /* ── Selected Region: explanation panel ── */
              <RegionExplanation
                region={selectedRegion}
                fields={fields}
                getRegionColor={getRegionColor}
              />

            ) : (
              /* ── No selection ── */
              <div className="no-selection">
                <p>Click a region or zone on the PDF, or click a field in the left panel.</p>
                <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
                  <strong>Regions:</strong> Click colored overlays to see AI detection details.<br />
                  <strong>Select mode:</strong> Click zones to select, drag to move, corners to resize.<br />
                  <strong>Draw mode:</strong> Click+drag on PDF to create new zones.
                </p>
              </div>
            )}

            {/* Error display */}
            {error && (
              <div className="error-banner" style={{ marginTop: '12px' }}>
                <p style={{ color: '#ef4444', fontSize: '12px' }}>{error}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Field Form Modal */}
      {showFieldForm && (
        <TemplateFieldForm
          field={editingField ? { ...editingField, templateId: template?.id } : { templateId: template?.id }}
          onSave={handleFieldSave}
          existingFieldKeys={fields.map(f => f.field_key)}
          libraryRows={fieldLibrary}
          templateFields={fields}
          documentType={template?.document_type || ''}
          onUseLibraryField={handleUseLibraryField}
          onCancel={() => {
            setShowFieldForm(false);
            setEditingField(null);
          }}
        />
      )}
    </div>
  );
};

export default TemplateEditorPage;